import { Button, Typography, Divider, Modal, notification } from 'antd';
import {
	UserAddOutlined,
	InfoCircleOutlined,
	SettingOutlined,
	DeleteOutlined,
} from '@ant-design/icons';
import { PhoneOutlined, SoundOutlined } from '@ant-design/icons';
import { CustomModal } from '@/components/CustomModal/CustomModal';

const { Text } = Typography;

interface UnconfiguredAgentModalProps {
	visible: boolean;
	onCancel: () => void;
	onContinue: () => void;
	onDelete?: () => void;
	agentType: 'inbound' | 'outbound';
	unconfiguredCount: number;
}

const UnconfiguredAgentModal: React.FC<UnconfiguredAgentModalProps> = ({
	visible,
	onCancel,
	onContinue,
	onDelete,
	agentType,
	unconfiguredCount,
}) => {
	const isInbound = agentType === 'inbound';
	const agentIcon = isInbound ? <PhoneOutlined /> : <SoundOutlined />;
	const agentTypeName = isInbound ? 'Inbound' : 'Outbound';

	const handleDelete = () => {
		Modal.confirm({
			title: 'Delete Unconfigured Agent',
			content: `Are you sure you want to delete this unconfigured ${agentTypeName.toLowerCase()} agent? This action cannot be undone.`,
			okText: 'Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				try {
					onDelete?.();
					notification.success({
						message: 'Agent Deleted',
						description: `The unconfigured ${agentTypeName.toLowerCase()} agent has been successfully deleted.`,
						placement: 'topRight',
					});
				} catch {
					notification.error({
						message: 'Failed to Delete Agent',
						description: 'There was an error deleting the agent. Please try again.',
						placement: 'topRight',
					});
				}
			},
		});
	};

	return (
		<CustomModal
			isModalOpen={visible}
			cancelAction={onCancel}
			confirmAction={onContinue}
			title={`Use Unconfigured ${agentTypeName} Agent`}
			description={`You have ${unconfiguredCount} unconfigured ${agentTypeName.toLowerCase()} agent${unconfiguredCount > 1 ? 's' : ''} available`}
			icon={<UserAddOutlined />}
			footer={null}
			width={700}
			className="unconfigured-agent-modal"
		>
			<div className="p-6">
				<div className="space-y-4 mb-6">
					<div className="flex items-start gap-3">
						<div className="bg-gray-100 p-2 rounded-lg mt-1">
							<InfoCircleOutlined className="text-lg text-gray-600" />
						</div>
						<div>
							<Text strong className="block mb-1">
								What you'll get
							</Text>
							<Text type="secondary" className="block">
								You can use one of your unconfigured {agentTypeName.toLowerCase()} agents instead of
								purchasing a new one. This will help you save on subscription costs.
							</Text>
						</div>
					</div>

					<div className="flex items-start gap-3">
						<div className="bg-gray-100 p-2 rounded-lg mt-1">
							<SettingOutlined className="text-lg text-gray-600" />
						</div>
						<div>
							<Text strong className="block mb-1">
								Configuration
							</Text>
							<Text type="secondary" className="block">
								After selecting an unconfigured agent, you'll be able to configure its settings,
								customize its behavior, and manage it according to your requirements.
							</Text>
						</div>
					</div>
				</div>

				<Divider className="my-4" />

				<div className="flex justify-between items-center">
					{onDelete && (
						<Button type="text" danger icon={<DeleteOutlined />} onClick={handleDelete}>
							Delete Unconfigured Agent
						</Button>
					)}
					<div className="flex gap-3">
						<Button onClick={onCancel}>Cancel</Button>
						<Button type="primary" onClick={onContinue} icon={agentIcon}>
							Use Unconfigured Agent
						</Button>
					</div>
				</div>
			</div>
		</CustomModal>
	);
};

export default UnconfiguredAgentModal;
